import { useState } from 'react';
import { Check, ChevronLeft } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card/Card';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { cn } from '@/lib/utils';
import { useLabeling } from '../context/useLabeling';
import type { ImageLabel } from '../types/LabelingTypes';
import { LABEL_FILL } from '../utils/labelStateClasses';
import { PartPickerSheet } from './PartPickerSheet';

interface LegendChipProps {
  color: ImageLabel;
  count: number;
  label: string;
}

function LegendChip({ color, count, label }: LegendChipProps) {
  return (
    <div className="flex items-center gap-1 text-sm">
      <div className={cn('w-2.5 h-2.5 rounded-full', LABEL_FILL[color])} />
      <span className="font-semibold text-foreground">{count}</span>
      <span className="text-muted-foreground">{label}</span>
    </div>
  );
}

interface LabelingToolbarProps {
  onChangeTool?: () => void;
}

export function LabelingToolbar({ onChangeTool }: LabelingToolbarProps = {}) {
  const {
    mode,
    setMode,
    statusFilter,
    setStatusFilter,
    seriesFilter,
    setSeriesFilter,
    stats,
    progressPct,
    items,
    filtered,
    acceptAllSuggestions,
    resetAnnotations,
  } = useLabeling();
  const { t } = useTranslation();
  const [resetting, setResetting] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  // The reset now deletes every label server-side (one request per labeled image), so it is
  // no longer instantaneous — keep the trigger disabled until the fan-out settles. The flag
  // stays local, like LabelPanel's `classifying`, since no other component needs it.
  const handleReset = async () => {
    if (resetting) return;
    setResetting(true);
    try {
      await resetAnnotations();
    } catch (err: unknown) {
      console.warn('[LabelingToolbar] Reset failed:', err);
    } finally {
      setResetting(false);
    }
  };

  const pendingCnn = filtered.filter((i) => i.cnn !== null).length;

  return (
    <Card padding="none" className="px-4 py-3">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        {/* Left group */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Change tool affordance — low-emphasis, shown only when handler provided */}
          {onChangeTool && (
            <Button variant="ghost" size="sm" onClick={onChangeTool}>
              <ChevronLeft aria-hidden />
              {t('labeling.selector.changeTool')}
            </Button>
          )}

          {/* Mode segmented control */}
          <SegmentedControl
            size="sm"
            aria-label={t('labeling.toolbar.modeLabel')}
            value={mode}
            onChange={setMode}
            options={[
              { value: 'focus', label: t('labeling.toolbar.modeFocus') },
              { value: 'grid', label: t('labeling.toolbar.modeGrid') },
              { value: 'dichotomous', label: t('labeling.toolbar.modeDichotomous') },
            ]}
          />

          {/* Part filter — a compact trigger opening the shared picker in a sheet */}
          <PartPickerSheet
            items={items}
            selectedPartId={seriesFilter === 'all' ? null : seriesFilter}
            onSelect={(partId) => setSeriesFilter(partId ?? 'all')}
          />

          {/* Status filter */}
          <SegmentedControl
            size="sm"
            aria-label={t('labeling.toolbar.statusLabel')}
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { value: 'all', label: t('labeling.toolbar.statusAll') },
              { value: 'todo', label: t('labeling.toolbar.statusTodo') },
              { value: 'done', label: t('labeling.toolbar.statusDone') },
            ]}
          />

          {/* Accept all AI suggestions */}
          <Button
            variant="outline"
            size="sm"
            disabled={pendingCnn === 0}
            onClick={acceptAllSuggestions}
          >
            <Check aria-hidden />
            {t('labeling.cnn.acceptAll')}
            {pendingCnn > 0 && (
              <Badge tone="success" size="sm" className="ml-0.5 font-mono font-bold">
                {pendingCnn}
              </Badge>
            )}
          </Button>
        </div>

        {/* Right group: legend + progress + DEV reset */}
        <div className="flex items-center gap-4 flex-wrap">
          {/* 3 legend chips */}
          <div className="flex items-center gap-3">
            <LegendChip color="ok" count={stats.ok} label={t('labeling.legend.ok')} />
            <LegendChip color="worn" count={stats.worn} label={t('labeling.legend.worn')} />
            <LegendChip
              color="unreadable"
              count={stats.unreadable}
              label={t('labeling.legend.unreadable')}
            />
          </div>

          {/* Progress */}
          <div className="flex flex-col items-end gap-1 min-w-[120px]">
            <span className="text-sm text-muted-foreground">
              {t('labeling.toolbar.progress', { done: stats.done, total: stats.total })}
            </span>
            <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full rounded-full bg-linear-to-r from-ok to-primary"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>

          {/* DEV reset — only in development builds */}
          {import.meta.env.DEV && (
            <>
              <Button
                variant="destructive"
                size="sm"
                loading={resetting}
                onClick={() => setConfirmReset(true)}
              >
                {t('labeling.dev.resetButton')}
              </Button>
              <ConfirmDialog
                open={confirmReset}
                title={t('labeling.dev.resetTitle')}
                description={t('labeling.dev.resetBody')}
                confirmLabel={t('labeling.dev.resetConfirm')}
                cancelLabel={t('labeling.dev.resetCancel')}
                onConfirm={() => {
                  setConfirmReset(false);
                  void handleReset();
                }}
                onCancel={() => setConfirmReset(false)}
              />
            </>
          )}
        </div>
      </div>
    </Card>
  );
}
